import React from 'react'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import Icon from 'blocks.simple/icon/icon'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import { cn } from 'ethcss'
import styles from './catalog__list.jcss'
import item from 'blocks.simple/sortableItem/item.jcss'
import translate from 'core/translate'
import helpers from 'core/helpers'
import { getThemeStyleValue } from 'theme/colors'
import { defaultThemeStyles } from 'blocks.simple/icon/icon.theme'
import {
    CatalogCellValueGetter,
    CatalogField,
    CatalogItem,
    CatalogListItem,
    CatalogListItemData,
    getCatalogValue,
    ICatalogListProps,
    toCatalogCellNode,
} from '../catalog.types'

type Option = CatalogField

const DragHandle = SortableHandle(() => (
    <div className={item.sortableIcon} style={{ position: 'absolute' }}>
        <Icon
            size={24}
            type="sortable_handle"
            color={getThemeStyleValue('ui', 'icon', 'disable') || defaultThemeStyles.disable}
        />
    </div>
))

const SortableItem = SortableElement(
    ({
        p_,
        getCellValue,
        listItemData,
        nestedLevel,
    }: {
        p_: ICatalogListProps
        getCellValue: CatalogCellValueGetter
        listItemData: CatalogListItemData
        nestedLevel: number
    }) => {
        const { listItem, index, folderKey, options } = listItemData
        return (
            <li style={{ position: 'relative' }} className={item.listItem} key={index}>
                {p_.multipleSelect && (
                    <div style={{ width: '6%' }}>
                        <div className={item.tableCell}>
                            <Checkbox
                                onClick={(selected: boolean, e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    p_.onSelect({ selected, item: listItem })
                                }}
                                checked={listItem.__view.selected}
                                mod={!p_.template.hideActiveRowColor ? 'circleInv' : 'circle'}
                                disabled={
                                    !!(p_.disableFolderSelection && p_.isFolder(listItem)) ||
                                    p_.disableMultiSelect(listItem)
                                }
                            />
                        </div>
                    </div>
                )}

                {options.map((option: Option, index: number) => (
                    <div
                        key={`catalogListItem_${folderKey}_${listItem.id}_${index}`}
                        style={getCellWidth(p_.template.templateCellsWidth, index)}
                    >
                        {getCellValue(listItem, option.id, listItem.__view.selected, nestedLevel)}
                    </div>
                ))}
                <DragHandle />
            </li>
        )
    }
)

const getCellWidth = (
    cellsWidthData: ICatalogListProps['template']['templateCellsWidth'],
    index: number
): React.CSSProperties => {
    if (!Array.isArray(cellsWidthData) || !cellsWidthData.length) return { width: 'auto' }

    if (index <= cellsWidthData.length - 1) {
        return cellsWidthData[index]
    }

    return { width: 'auto' }
}

const renderTree = (p_: ICatalogListProps, options: Option[], getCellValue: CatalogCellValueGetter) => {
    const normalizeItem = (item: CatalogItem, index: number): CatalogListItem => {
        return {
            ...item,
            __view: {
                ...item.__view,
                selected: !!(item.__view && item.__view.selected),
                isOpen: !!(item.__view && item.__view.isOpen),
                index,
            },
            children: item.children
                ? item.children.map((child, childIndex) => normalizeItem(child, childIndex))
                : undefined,
        }
    }

    const folders = helpers.deepCopy(p_.list).map((catalogItem, index) => {
        return normalizeItem(catalogItem, index)
    })
    const nested = folders.filter((catalogItem, _index, catalogItems) => {
        const nestedKey = catalogItem.__view.nestedKey
        const parent = nestedKey
            ? catalogItems.find(
                  (parentItem) =>
                      p_.template.isFolder(parentItem) && parentItem.id === getCatalogValue(catalogItem, nestedKey)
              )
            : undefined
        if (!parent) return true
        ;(parent.children = parent.children || []).push(catalogItem)
        return false
    })

    const elements: React.ReactNode[] = []

    const loopOptions = (data: CatalogListItem[], nestedLevel: number) => {
        data.forEach((elem, index) => {
            const isFolder = p_.isFolder(elem) ? 'folder' : 'item'

            elements.push(
                <SortableItem
                    p_={{ ...p_ }}
                    getCellValue={getCellValue}
                    key={`catalogListRow_${elem.__view.index}`}
                    listItemData={{ listItem: elem, index: elem.__view.index, isFolder, options }}
                    nestedLevel={nestedLevel}
                    index={index}
                />
            )

            if (elem.children && elem.__view.isOpen) {
                const counter = nestedLevel + 1
                loopOptions(elem.children, counter)
            }
        })
    }

    loopOptions(nested, 0)
    return elements
}

const SortableList = SortableContainer(
    ({
        p_,
        options,
        getCellValue,
        permissionDisable,
    }: {
        p_: ICatalogListProps
        options: CatalogField[]
        getCellValue: CatalogCellValueGetter
        permissionDisable: boolean
    }) => (
        <div>
            {p_.fakeLine || p_.allowEmptyList ? (
                <li key={`catalogListRow_${'fake'}`} className={item.listItem}>
                    {p_.multipleSelect && (
                        <div style={{ width: '6%' }} className={item.choice}>
                            <Checkbox
                                checked={false}
                                onClick={(_selected, e) => e.stopPropagation()}
                                mod={'circle'}
                                disabled={true}
                            />
                        </div>
                    )}
                    {options.map((option, index) => (
                        <div
                            key={`catalogListItem_fake_${index}`}
                            style={getCellWidth(p_.template.templateCellsWidth, index)}
                        >
                            {p_.fakeLine ? getCellValue(p_.fakeLine, option.id, true) : null}
                        </div>
                    ))}
                    {p_.template.onDeleteListItem && <div></div>}
                </li>
            ) : null}

            {renderTree(p_, options, getCellValue)}
        </div>
    )
)

const CatalogSortableList = (p_: ICatalogListProps) => {
    const getCellValue: CatalogCellValueGetter = (listItem, id, active, nestedLevel) => {
        const cellRender = p_.template.list

        if (cellRender && cellRender[id]) {
            return cellRender[id](listItem, active, p_, nestedLevel)
        } else {
            return toCatalogCellNode(getCatalogValue(listItem, id))
        }
    }

    const options: CatalogField[] = p_.options ? p_.options.filter((option) => !option.hide) : []

    return (
        <ul className={cn(item.list, styles.wrapper, p_.template.tableWrapper)}>
            <li className={`${item.listItem} ${item.header}`}>
                {p_.multipleSelect && (
                    <div style={{ width: '6%' }} className={item.caption}>
                        {translate('choice')}
                    </div>
                )}
                {p_.treeView && <div />}
                {options.map((option, index) => (
                    <div
                        key={`catalogListHeader_${index}`}
                        style={getCellWidth(p_.template.templateCellsWidth, index)}
                        className={item.caption}
                    >
                        {option.name}
                    </div>
                ))}
                {p_.template.onDeleteListItem && <div style={{ width: '32px' }} />}
            </li>

            <SortableList
                p_={p_}
                options={options}
                getCellValue={getCellValue}
                onSortEnd={p_.onSortEnd}
                useDragHandle={true}
                permissionDisable={false}
            />
        </ul>
    )
}

CatalogSortableList.defaultProps = {
    multipleSelect: true,
}

export default CatalogSortableList
